import { useAuth } from "@/components/Auth";
import { useCart } from "@/components/Cart";
import CompletedLineItems from "@/components/CompletedLineItems";
import CountySelect, { CountyMask } from "@/components/CountySelect";
import GuardNameModule from "@/components/GuardNameModule";
import GuardPropertyModule from "@/components/GuardPropertyModule";
import { useInvoices } from "@/components/Invoice";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { GuardRecordFoundEvent, GuardRecordSubscriptionType, guardRecordSubscriptionType, notificationType, NotificationType, User } from "@/types";
import { ActionIcon, Alert, Box, Button, Grid, Group, Indicator, LoadingOverlay, Modal, Paper, ScrollArea, Stack, Table, Text, TextInput, Title, Tooltip, useCombobox } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconDownload, IconExclamationCircle, IconPlus, IconRefresh, IconShoppingCart, IconShoppingCartPlus, IconTrash } from "@tabler/icons-react";
import dayjs from 'dayjs';
import { useEffect, useState } from "react";

interface GuardRecordSubscription {
    guardSubscriptionId: number
    guardRecordSubscriptionType: GuardRecordSubscriptionType
}

interface NotificationMethod {
    notificationMethodId: number
    notificationMethodType: NotificationType
}

// interface GuardRecordEventAlert {
//     id: string;
//     county: string;
//     countyCodeContainerName: string;
//     parsedDocumentName: string;
//     documentNumber: string;
//     createdAt: string;
//     matchedValue: string;
// }

const Dashboard: React.FC = () => {
    useEffect(() => {
        document.title = "Dashboard - Guard Dog Real Estate Protection Service";
    }, []);

    const { currentUser } = useAuth();
    const { addToCart, isInCart, loading: cartLoading } = useCart();
    const { lineItems: purchasedDocuments, isLineItemPurchased: isDocumentPurchased, getLineItemId: getPurchasedDocumentId } = useInvoices();
    const [user, setUser] = useState(currentUser);
    const [addingToCartEventId, setAddingToCartEventId] = useState<number | null>(null);
    const [addGuardRecordSubscriptionModalOpened, GuardRecordModalHandlers] = useDisclosure(false)
    const [notificationMethodModalOpened, notificationMethodModalHandlers] = useDisclosure(false)
    const [resendNotificationMethodVerificationModalOpened, resendNotificationMethodVerificationModalHandlers] = useDisclosure(false);
    const [selectedGuardRecordSubscriptionType, setSelectedGuardRecordSubscriptionType] = useState<GuardRecordSubscriptionType>(guardRecordSubscriptionType.apn);
    const [selectedNotificationType, setNotificationType] = useState<NotificationType>(notificationType.email);
    const [guardedNames, setGuardedNames] = useState<{ county: string, name: string }[]>([]);
    const [tempGuardedNames, setTempGuardedNames] = useState<{ county: string, name: string }[]>([]);
    const [guardedProperties, setGuardedProperties] = useState<{ county: string, apn: string, description?: string }[]>([]);
    const [tempGuardedProperties, setTempGuardedProperties] = useState<{ county: string, apn: string, description?: string }[]>([]);
    const [county, setCounty] = useState<CountyMask | null>(null);
    const [apnsLoading, setApnsLoading] = useState<boolean>(false);
    const [namesLoading, setNamesLoading] = useState<boolean>(false);
    // const [smsLoading, setSmsLoading] = useState<boolean>(false);
    const [emailLoading, setEmailLoading] = useState<boolean>(false);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
    const [email, setEmail] = useState<string>('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [selectedRemoveGuardRecordSubscription, setSelectedRemoveGuardRecordSubscription] = useState<GuardRecordSubscription | null>(null);
    const [selectedRemoveNotificationMethod, setSelectedRemoveNotificationMethod] = useState<NotificationMethod | null>(null);
    const [selectedNotificationMethod, setSelectedNotificationMethod] = useState<NotificationMethod | null>(null);
    const [guardRecordSubscriptionRemoveModalOpened, guardRecordSubscriptionRemoveModalHandlers] = useDisclosure(false);
    const [notificationMethodRemoveModalOpened, notificationMethodRemoveModalHandlers] = useDisclosure(false);
    const apnSearchCombobox = useCombobox({
        onDropdownClose: () => {
            apnSearchCombobox.resetSelectedOption();
        }
    });

    const addressSearchCombobox = useCombobox({
        onDropdownClose: () => {
            addressSearchCombobox.resetSelectedOption();
        }
    });

    const nameSearchCombobox = useCombobox({
        onDropdownClose: () => {
            nameSearchCombobox.resetSelectedOption();
        }
    });

    useEffect(() => {
        if (currentUser) {
            setUser({ ...currentUser });
        }
    }, [currentUser]);

    useEffect(() => {
        if (user) {
            window.localStorage.setItem('user', JSON.stringify(user));
            setGuardedProperties(user.apnSubscriptions.map((item) => ({ county: item.county, apn: item.apn, description: item.description })));
            setGuardedNames(user.nameSubscriptions.map((item) => ({ county: item.county, name: item.name })));
        } else {
            window.localStorage.removeItem('user');
        }
    }, [user])

    
    const onAddGuardRecordSubscriptionModalClose = () => {
        if(tempGuardedNames.length !== guardedNames.length || tempGuardedProperties.length !== guardedProperties.length){
           modals.openConfirmModal({
                title: 'Discard Changes?',
                centered: true,
                children: <Text>You are about to exit this screen without guarding your selected items. Are you sure?</Text>,
                labels: { confirm: 'Discard Changes', cancel: 'Keep Adding' },
                confirmProps: { color: 'pq-red' },
                onCancel: () => { },
                onConfirm: () => {
                    setTempGuardedNames([...guardedNames]);
                    setTempGuardedProperties([...guardedProperties]);
                    setCounty(null);
                    GuardRecordModalHandlers.close();
                }
            });
        } else {
            setCounty(null);
            GuardRecordModalHandlers.close();   
        }
    }

    const onAddGuardApn = async (guardRecordSubscription: { county: string, apn: string, description?: string }) => {
        setTempGuardedProperties([...tempGuardedProperties, { county: guardRecordSubscription.county, apn: guardRecordSubscription.apn.trim(), description: guardRecordSubscription.description }]);
    }
    const onAddGuardName = (guardRecordSubscription: { county: string, name: string }) => {
        setTempGuardedNames([...tempGuardedNames, { county: guardRecordSubscription.county, name: guardRecordSubscription.name.trim() }]);
    }

    const onConfirmAddGuardRecordSubscription = async () => {
        try{
            switch (selectedGuardRecordSubscriptionType) {
                case guardRecordSubscriptionType.apn:
                    { setApnsLoading(true);
                    const newUser: User = await api.post('/user/subscriptions/apns/list/add', { guardApnSubscriptions: tempGuardedProperties.filter((item) => !guardedProperties.some((existing) => existing.county === item.county && existing.apn === item.apn)) });
                    if (user) {
                        setUser({ ...newUser });
                        setGuardedProperties(newUser.apnSubscriptions.map((item) => ({ county: item.county, apn: item.apn, description: item.description })));
                    }
                    break; }
                case guardRecordSubscriptionType.name:
                    { setNamesLoading(true);
                    const updatedUser: User = await api.post('/user/subscriptions/names/list/add', { guardNameSubscriptions: tempGuardedNames.filter((item) => !guardedNames.some((existing) => existing.county === item.county && existing.name === item.name)) });
                    if (user) {
                        setUser({ ...updatedUser });
                        setGuardedNames(updatedUser.nameSubscriptions.map((item) => ({ county: item.county, name: item.name })));
                    }
                    break; }
            }
        }
        catch (error) {
            console.error(error);
            notifications.show({ title: 'Error', message: `Failed to add Guard ${selectedGuardRecordSubscriptionType} Subscriptions`, color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
        } finally {
            setTempGuardedNames([]);
            setTempGuardedProperties([]);
            setApnsLoading(false);
            setNamesLoading(false);
            GuardRecordModalHandlers.close();
        }
    }
    const onAddGuardRecordSubscriptions = (alertSubscriptionType: GuardRecordSubscriptionType) => {
        setSelectedGuardRecordSubscriptionType(alertSubscriptionType);
        setTempGuardedNames([...guardedNames]);
        setTempGuardedProperties([...guardedProperties]);
        GuardRecordModalHandlers.open();
    }
    const onSelectedRemoveGuardRecordSubscription = (guardRecordSubscription: GuardRecordSubscription) => {
        setSelectedRemoveGuardRecordSubscription(guardRecordSubscription);
        guardRecordSubscriptionRemoveModalHandlers.open();
    }
    const onRemoveGuardRecordSubscription = async () => {
        guardRecordSubscriptionRemoveModalHandlers.close();
        switch (selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType) {
            case guardRecordSubscriptionType.apn:
                setApnsLoading(true);
                break;
            case guardRecordSubscriptionType.name:
                setNamesLoading(true);
                break;
        }
        try {
            const newUser: User = await api.post('/user/subscriptions/remove', selectedRemoveGuardRecordSubscription);
            if (user) {
                setUser({ ...newUser });
                switch (selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType) {
                    case guardRecordSubscriptionType.apn:
                        setGuardedProperties(newUser.apnSubscriptions.map((item) => ({ county: item.county, apn: item.apn, description: item.description })));
                        break;
                    case guardRecordSubscriptionType.name:
                        setGuardedNames(newUser.nameSubscriptions.map((item) => ({ county: item.county, name: item.name })));
                        break;
                }
            }
        } catch (error) {
            console.error(error);
            notifications.show({ title: 'Error', message: `Failed to remove Guard ${selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType} Subscription`, color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
        } finally {
            switch (selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType) {
                case guardRecordSubscriptionType.apn:
                    setApnsLoading(false);
                    break;
                case guardRecordSubscriptionType.name:
                    setNamesLoading(false);
                    break;
            }
        }
    }
    const onSelectedRemoveNotificationMethod = (notificationMethod: NotificationMethod) => {
        setSelectedRemoveNotificationMethod(notificationMethod);
        notificationMethodRemoveModalHandlers.open();
    }
    const onNotificationMethodChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumberError(null);
        setEmailError(null);
        const value = event.currentTarget.value;
        const name = event.currentTarget.name;
        if (selectedNotificationType === notificationType.email && name === 'email') {
            if (value.length === 0) {
                setEmailError('Email is required');
            }
            if (!(/^\S+@\S+$/.test(value))) {
                setEmailError('Invalid Email');
            }
            setEmail(value);
        }
        if (selectedNotificationType === notificationType.sms && name === 'phoneNumber') {
            if (value.trim().length === 0) {
                setPhoneNumberError('Phone Number is required');
            }
            setPhoneNumber(value);
        }
    }
    const onNotificationMethodModalClose = () => {
        setPhoneNumber('');
        setEmail('');
        setPhoneNumberError(null);
        setEmailError(null);
        notificationMethodModalHandlers.close();
    }
    const onAddNotificationMethod = async () => {
        switch (selectedNotificationType) {
            case notificationType.email:
                if (emailError) return;
                if (email.trim().length === 0) {
                    setEmailError('Email is required');
                    return;
                }
                notificationMethodModalHandlers.close();
                setEmailLoading(true);
                try {
                    const newUser: User = await api.post('/user/notifications/add', { email: email, notificationMethodType: selectedNotificationType });
                    if (user) {
                        setUser({ ...newUser });
                    }
                } catch (error) {
                    console.error(error);
                    notifications.show({ title: 'Error', message: 'Failed to add Email Notification Method', color: 'red', position: 'top-center',"aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
                }
                finally {
                    setEmailLoading(false);
                }
                break;
            // case notificationType.sms:
            //     if (phoneNumberError) return;
            //     if (phoneNumber.trim().length === 0) {
            //         setPhoneNumberError('Phone Number is required');
            //         return;
            //     }
            //     notificationMethodModalHandlers.close();
            //     setSmsLoading(true);
            //     try {
            //         const newUser: User = await api.post('/user/notifications/add', { phoneNumber: phoneNumber, notificationMethodType: selectedNotificationType });
            //         if (user) {
            //             setUser({ ...newUser });
            //         }
            //     } catch (error) {
            //         console.error(error);
            //         notifications.show({ title: 'Error', message: 'Failed to add SMS Notification Method', color: 'red', position: 'top-center',"aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
            //     }
            //     finally {
            //         setSmsLoading(false);
            //     }
            //     break;
        }
    }
    const onAddNotificationMethods = (notificationMethodType: NotificationType) => {
        setNotificationType(notificationMethodType);
        notificationMethodModalHandlers.open();
    }
    const onRemoveNotificationMethod = async () => {
        notificationMethodRemoveModalHandlers.close();
        switch (selectedRemoveNotificationMethod?.notificationMethodType) {
            case notificationType.email:
                setEmailLoading(true);
                break;
            // case notificationType.sms:
            //     setSmsLoading(true);
            //     break;
        }
        try {
            const newUser: User = await api.post('/user/notifications/remove', selectedRemoveNotificationMethod);
            if (user) {
                setUser({ ...newUser });
            }
        } catch (error) {
            console.error(error);
            notifications.show({ title: 'Error', message: `Failed to remove ${selectedRemoveNotificationMethod?.notificationMethodType} Notification Method`, color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
        } finally {
            switch (selectedRemoveNotificationMethod?.notificationMethodType) {
                case notificationType.email:
                    setEmailLoading(false);
                    break;
                // case notificationType.sms:
                //     setSmsLoading(false);
                //     break;
            }
        }
    }
    const onAddToCart = async (event: GuardRecordFoundEvent) => {
        setAddingToCartEventId(event.id);
        await addToCart(event.id);
        setAddingToCartEventId(null);
    };

    const onDownloadEventDocument = async (event: GuardRecordFoundEvent, isPurchased: boolean) => {
        // Use different endpoints for purchased vs preview documents
        let endpoint: string;
        if (isPurchased) {
            const purchasedDocumentId = getPurchasedDocumentId(event.documentNumber, event.county);
            if (!purchasedDocumentId) {
                notifications.show({ title: 'Error', message: 'Purchased document not found', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
                return;
            }
            endpoint = `/document/download-purchased/${purchasedDocumentId}`;
        } else {
            endpoint = `/document/download?containerName=${event.countyCodeContainerName}&fileName=${event.parsedDocumentName}`;
        }

        await downloadFile(endpoint, `${event.documentNumber}.pdf`);
    }
    const onArchiveEvent = async (event: GuardRecordFoundEvent) => {
        try {
            const newUser: User = await api.post(`/user/events/archive`, { eventId: event.id });
            if (user) {
                setUser({ ...newUser });
            }
        }
        catch (error) {
            console.error(error);
            notifications.show({ title: 'Error', message: 'Failed to Archive Event', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
        }
    }
    const onResendVerification = async (notificationMethodId: number) => {
        try {
            await api.post('user/notifications/verify/resend', { notificationMethodId });
        } catch (error) {
            console.error(error);
            notifications.show({ title: 'Error', message: 'Failed to resend verification email', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
        } finally {
            setEmailLoading(false);
        }
    }
    const getEventsTableElements = () => {
        const events: GuardRecordFoundEvent[] = [];
        user?.apnSubscriptions?.forEach((guardApnSubscription) => {
            if (guardApnSubscription.events?.length) {
                guardApnSubscription.events.forEach((event) => {
                    if(event.isArchived){
                        return;
                    }
                    events.push({
                        ...event,
                        county: guardApnSubscription.county,
                        matchedValue: guardApnSubscription.apn,
                    });
                });
            }
        });
        user?.nameSubscriptions?.forEach((guardNameSubscription) => {
            if (guardNameSubscription.events?.length) {
                guardNameSubscription.events.forEach((event) => {
                    if(event.isArchived){
                        return;
                    }
                    events.push({
                        ...event,
                        county: guardNameSubscription.county,
                        matchedValue: guardNameSubscription.name,
                    });
                });
            }
        });
        events.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)));
        return events.map((event) => {
            const inCart = isInCart(event.documentNumber, event.county);
            const isPurchased = isDocumentPurchased(event.documentNumber, event.county);
            const isAddingThisEvent = addingToCartEventId === event.id;

            // Determine button state for cart/purchase
            const renderCartButton = () => {
                if (isPurchased) {
                    return (
                        <Tooltip label="Document purchased">
                            <ActionIcon aria-label="Document purchased" radius='xl' size='sm' color="teal" variant="light" disabled>
                                <IconCheck size={15} />
                            </ActionIcon>
                        </Tooltip>
                    );
                }
                if (inCart) {
                    return (
                        <Tooltip label="Already in cart">
                            <ActionIcon aria-label="Already in cart" radius='xl' size='sm' color="gray" variant="light" disabled>
                                <IconShoppingCart size={15} />
                            </ActionIcon>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip label="Add to cart">
                        <ActionIcon
                            aria-label="Add to cart"
                            onClick={() => onAddToCart(event)}
                            radius='xl'
                            size='sm'
                            color="pq-red"
                            loading={isAddingThisEvent}
                            disabled={cartLoading}
                        >
                            <IconShoppingCartPlus size={15} />
                        </ActionIcon>
                    </Tooltip>
                );
            };

            return (
                <Table.Tr key={event.id}>
                    <Table.Td>{event.county}</Table.Td>
                    <Table.Td>{event.documentDate}</Table.Td>
                    <Table.Td>{event.documentNumber}</Table.Td>
                    <Table.Td>{event.matchedValue}</Table.Td>
                    <Table.Td>{event.matchedValue}</Table.Td>
                    <Table.Td>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                            {renderCartButton()}
                            <Tooltip label={isPurchased ? "Download full document" : "Download preview"}>
                                <ActionIcon aria-label={isPurchased ? "Download full document" : "Download preview"} onClick={() => onDownloadEventDocument(event, isPurchased)} radius='xl' size='sm' color="pq-red">
                                    <IconDownload size={15} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Archive alert">
                                <ActionIcon aria-label="Archive alert" onClick={() => onArchiveEvent(event)} radius='xl' size='sm' color="pq-red">
                                    <IconTrash size={15} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </Table.Td>
                </Table.Tr>
            );
        });
    }

    const hasEvents = () => {
        return user?.apnSubscriptions?.some((apnSubs) => apnSubs.events?.some((event) => !event.isArchived)) || user?.nameSubscriptions?.some((nameSubs) => nameSubs.events?.some((event) => !event.isArchived));
    }
    // const hasArchivedEvents = () => {
    //     return user?.apnSubscriptions?.some((apnSubs) => apnSubs.events?.some((event) => event.isArchived)) || user?.nameSubscriptions?.some((nameSubs) => nameSubs.events?.some((event) => event.isArchived));
    // }
    return (
        <>
            <Title order={1} c='pq-red' style={{ fontFamily: 'Elston, sans-serif', letterSpacing: '2px' }}>Dashboard</Title>
            <Grid>
                <Grid.Col span={{ xs: 12, md: 12 }}>
                    <Title order={2}>Notification Methods</Title>
                    <Grid>
                        <Grid.Col span={{ xs: 12, md: 12 }}>
                            <Paper pos='relative' shadow="sm" p='md' radius='md' withBorder>
                                <LoadingOverlay visible={emailLoading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'sm' }}></LoadingOverlay>
                                <Stack align="start" justify="space-between">
                                    <Group w='100%' align="center" justify="space-between">
                                        <Title order={6}>EMAIL</Title>
                                        <ActionIcon aria-label="Add email notification method" onClick={() => onAddNotificationMethods(notificationType.email)} radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                    {
                                        (user?.emailNotificationMethods?.length ?? 0) > 0 ? user?.emailNotificationMethods?.map((emailNotificationMethod) => (
                                            <Group w='100%' align="center" justify="space-between" key={emailNotificationMethod.id}>
                                                {emailNotificationMethod.verified ?
                                                    <Box w='80%'>
                                                        <Text fz='xs'>Email Address: {emailNotificationMethod.email}</Text>
                                                        <Text truncate fz='xs'>Verified: {`${dayjs(emailNotificationMethod.verifiedAt).format('MM/DD/YYYY')}`}</Text>
                                                    </Box>
                                                    :
                                                    <Indicator w='80%' position="top-start" color='pq-red' ps={10}>
                                                        <Box w='100%'>
                                                            <Text fz='xs'>Email Address: {emailNotificationMethod.email}</Text>
                                                            <Text truncate fz='xs'>Not Verified</Text>
                                                        </Box>
                                                    </Indicator>}
                                                <Group align='center'>
                                                    {!emailNotificationMethod.verified && <ActionIcon aria-label={`Resend verification email to ${emailNotificationMethod.email}`} onClick={() => { setSelectedNotificationMethod({ notificationMethodId: emailNotificationMethod.id, notificationMethodType: notificationType.email }); resendNotificationMethodVerificationModalHandlers.open(); }} radius='xl' size='sm' color="pq-red"><IconRefresh size={15} /></ActionIcon>}
                                                    <ActionIcon aria-label={`Remove email notification method ${emailNotificationMethod.email}`} disabled={user.email === emailNotificationMethod.email} onClick={() => onSelectedRemoveNotificationMethod({ notificationMethodId: emailNotificationMethod.id, notificationMethodType: notificationType.email })} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                                </Group>
                                            </Group>
                                        )) : <Text>No Email Notification Methods</Text>
                                    }
                                </Stack>
                            </Paper>
                        </Grid.Col>
                        {/* <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Paper pos='relative' shadow="sm" p='md' radius='md' withBorder>
                                <LoadingOverlay visible={smsLoading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'sm' }}></LoadingOverlay>
                                <Stack align="start" justify="space-between">
                                    <Group w='100%' align="center" justify="space-between">
                                        <Title order={6}>SMS</Title>
                                        <ActionIcon aria-label="Add SMS notification method" onClick={() => onAddNotificationMethods(notificationType.sms)} disabled={true} radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                    {
                                        (user?.smsNotificationMethods?.length ?? 0) > 0 ? user?.smsNotificationMethods?.map((smsNotificationMethod) => (
                                            <Group w='100%' align="center" justify="space-between" key={smsNotificationMethod.id}>
                                                {smsNotificationMethod.verified ?
                                                    <Box w='80%'>
                                                        <Text fz='xs'>Phone Number: {smsNotificationMethod.phoneNumber}</Text>
                                                        <Text truncate fz='xs'>Verified: {`${dayjs(smsNotificationMethod.verifiedAt).format('MM/DD/YYYY')}`}</Text>
                                                    </Box>
                                                    :
                                                    <Indicator w='80%' position="top-start" color='pq-red' ps={10}>
                                                        <Box w='100%'>
                                                            <Text fz='xs'>Phone Number: {smsNotificationMethod.phoneNumber}</Text>
                                                        </Box>
                                                    </Indicator>}
                                                <Group align='center'>
                                                    {!smsNotificationMethod.verified && <ActionIcon aria-label={`Resend verification SMS to ${smsNotificationMethod.phoneNumber}`} onClick={() => { }} radius='xl' size='sm' color="pq-red"><IconRefresh size={15} /></ActionIcon>}
                                                    <ActionIcon aria-label={`Remove SMS notification method ${smsNotificationMethod.phoneNumber}`} onClick={() => onSelectedRemoveNotificationMethod({ notificationMethodId: smsNotificationMethod.id, notificationMethodType: notificationType.sms })} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                                </Group>
                                            </Group>
                                        )) : <Text>Coming Soon!</Text>
                                    }
                                </Stack>
                            </Paper>
                        </Grid.Col> */}
                    </Grid>
                </Grid.Col>
                <Grid.Col span={{ xs: 12 }}>
                    <Title order={2}>Alerts</Title>
                    <Paper pos='relative' shadow="sm" p='md' radius='md' withBorder>
                        <ScrollArea.Autosize mah={300} type="auto" offsetScrollbars="y">
                            <Stack>
                                {
                                    !hasEvents() &&
                                    <Alert title="No Alerts" icon={<IconExclamationCircle />} color="blue">
                                        You have no active alerts.</Alert>
                                }
                                {
                                    hasEvents() && <Table horizontalSpacing="xs" stickyHeader>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>County</Table.Th>
                                                <Table.Th>Recording Date</Table.Th>
                                                <Table.Th>Document Number</Table.Th>
                                                <Table.Th>Items You Registered for Guarding</Table.Th>
                                                <Table.Th>Item as Found in the Recorder's Index</Table.Th>
                                                <Table.Th>Actions</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {getEventsTableElements()}
                                        </Table.Tbody>
                                    </Table>}
                            </Stack>
                        </ScrollArea.Autosize>
                    </Paper>
                </Grid.Col>
                {purchasedDocuments.length > 0 && (
                    <Grid.Col span={{ xs: 12 }}>
                        <Title order={2}>Purchased Documents</Title>
                        <CompletedLineItems />
                    </Grid.Col>
                )}
                <Grid.Col span={{ xs: 12, md: 12 }}>
                    <Title order={2}>Items Being Guarded</Title>
                    <Grid>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Paper shadow="sm" p='md' radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Group tabIndex={0} role="button" onClick={() => onAddGuardRecordSubscriptions(guardRecordSubscriptionType.name)} w='100%' align="center" justify="space-between">
                                        <Title order={6}>Guard a Name</Title>
                                        <ActionIcon aria-label="Add guard record subscription for a name" radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                </Stack>
                            </Paper>
                            {(user?.nameSubscriptions?.length ?? 0) > 0 && <Paper mt={20} pos='relative' shadow="sm" p='md' radius='md' withBorder>
                                <LoadingOverlay visible={namesLoading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'sm' }}></LoadingOverlay>
                                <Stack align="start" justify="space-between">
                                    <Title order={6}>Names You're Guarding</Title>
                                    {user?.nameSubscriptions?.map((guardNameSubscription) => (
                                        <Group w='100%' align="center" justify="space-between" key={guardNameSubscription.id}>
                                            <Indicator styles={{ indicator: { display: guardNameSubscription.events?.length === 0 ? 'none' : 'flex' } }} w='80%' position="top-start" color='white' ps={10}>
                                                <Box w='100%'>
                                                    <Group justify="space-between">
                                                        <Box>
                                                            <Text fz='xs'>County: {guardNameSubscription.county}</Text>
                                                            <Tooltip label={guardNameSubscription.name} position='top' withArrow>
                                                                <Text truncate fz='xs'>Name: {guardNameSubscription.name}</Text>
                                                            </Tooltip>
                                                        </Box>
                                                        <Text ta='right' fz='xs'>Matches Found: {guardNameSubscription.events?.length ?? 0}</Text>
                                                    </Group>
                                                </Box>
                                            </Indicator>
                                            <Group align='center'>
                                                {/* <ActionIcon aria-label={`View details for ${guardNameSubscription.name}`} onClick={() => { }} radius='xl' size='sm' color="pq-red"><IconEye size={15} /></ActionIcon> */}
                                                <ActionIcon aria-label={`Remove guard record subscription for ${guardNameSubscription.name}`} onClick={() => onSelectedRemoveGuardRecordSubscription({ guardRecordSubscriptionType: guardRecordSubscriptionType.name, guardSubscriptionId: guardNameSubscription.id })} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                            </Group>
                                        </Group>
                                    ))}
                                </Stack>
                            </Paper>}
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Paper shadow="sm" p='md' radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Group tabIndex={0} role="button" onClick={() => onAddGuardRecordSubscriptions(guardRecordSubscriptionType.apn)} w='100%' align="center" justify="space-between">
                                        <Title order={6}>Guard a Property</Title>
                                        <ActionIcon aria-label="Add guard record subscription for a property" radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                </Stack>
                            </Paper>
                            {(user?.apnSubscriptions?.length ?? 0) > 0 && <Paper mt={20} pos='relative' shadow="sm" p='md' radius='md' withBorder>
                                <LoadingOverlay visible={apnsLoading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'sm' }}></LoadingOverlay>
                                <Stack align="start" justify="space-between">
                                    <Title order={6}>Properties You're Guarding</Title>
                                    {user?.apnSubscriptions?.map((guardApnSubscription) => (
                                        <Group w='100%' align="center" justify="space-between" key={guardApnSubscription.id}>
                                            <Indicator styles={{ indicator: { display: guardApnSubscription.events?.length === 0 ? 'none' : 'flex' } }} w='80%' position="top-start" color='white' ps={10}>

                                                <Box w='100%'>
                                                    <Group justify="space-between">
                                                        <Box>
                                                            <Text fz='xs'>County: {guardApnSubscription.county}</Text>
                                                            <Text truncate fz='xs'>APN: {guardApnSubscription.apn}</Text>
                                                            <Text truncate fz='xs'>Address: {guardApnSubscription.description ?? ''}</Text>
                                                        </Box>
                                                         <Text ta='right' fz='xs'>Matches Found: {guardApnSubscription.events?.length ?? 0}</Text>
                                                    </Group>
                                                </Box>
                                            </Indicator>
                                            <Group align='center'>
                                                {/* <ActionIcon aria-label={`View details for ${guardApnSubscription.apn}`} onClick={() => { }} radius='xl' size='sm' color="pq-red"><IconEye size={15} /></ActionIcon> */}
                                                <ActionIcon aria-label={`Remove guard record subscription for ${guardApnSubscription.apn}`} onClick={() => onSelectedRemoveGuardRecordSubscription({ guardRecordSubscriptionType: guardRecordSubscriptionType.apn, guardSubscriptionId: guardApnSubscription.id })} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                            </Group>
                                        </Group>
                                    ))}
                                </Stack>
                            </Paper>}
                        </Grid.Col>
                    </Grid>
                </Grid.Col>

            </Grid>
            <Modal size='xl' withCloseButton={true} opened={addGuardRecordSubscriptionModalOpened} onClose={onAddGuardRecordSubscriptionModalClose} title={`Guard A ${selectedGuardRecordSubscriptionType}`}>
                <Stack>
                    <Title order={6}>Step 1: Select a County</Title>
                    <CountySelect value={county?.name} onChange={(county) => setCounty(county)} />
                    {
                        county && selectedGuardRecordSubscriptionType === guardRecordSubscriptionType.apn && (
                            <>
                                <GuardPropertyModule
                                    key={addGuardRecordSubscriptionModalOpened ? 'guardPropertyModule' : ''}
                                    selectedCounty={county}
                                    onGuardProperty={onAddGuardApn}
                                    guardedCountyProperties={tempGuardedProperties}
                                />
                            </>

                        )
                    }
                    {
                        county && selectedGuardRecordSubscriptionType === guardRecordSubscriptionType.name && (
                            <>
                                <GuardNameModule
                                    key={addGuardRecordSubscriptionModalOpened ? 'guardNameModule' : ''}
                                    selectedCounty={county}
                                    onGuardName={onAddGuardName}
                                    guardedCountyNames={tempGuardedNames}
                                />
                            </>
                        )
                    }
                    <Group justify="flex-end" mt={20}>
                        <Button color='pq-red' onClick={onConfirmAddGuardRecordSubscription}>Guard Selected Items</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal withCloseButton={false} opened={notificationMethodModalOpened} onClose={onNotificationMethodModalClose} title={`Add ${selectedNotificationType} Notification Method`}>
                <Stack>
                    {
                        selectedNotificationType === notificationType.email && (
                            <TextInput
                                label="Email"
                                placeholder="Enter Email"
                                value={email}
                                type='email'
                                name='email'
                                onChange={onNotificationMethodChanges}
                                error={emailError}
                            ></TextInput>
                        )
                    }
                    {
                        selectedNotificationType === notificationType.sms && (
                            <TextInput
                                label="Phone Number"
                                placeholder="Enter Phone Number"
                                value={phoneNumber}
                                type='phone'
                                name='phoneNumber'
                                onChange={onNotificationMethodChanges}
                                error={phoneNumberError}
                            ></TextInput>
                        )
                    }
                    <Group align='center' justify='space-between'>
                        <Button w='45%' color='pq-gray' onClick={onNotificationMethodModalClose}>Cancel</Button>
                        <Button w='45%' color='pq-red' onClick={onAddNotificationMethod}>{`Add ${selectedNotificationType}`}</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal onClose={guardRecordSubscriptionRemoveModalHandlers.close} opened={guardRecordSubscriptionRemoveModalOpened} title={`Remove Guard ${selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType} Subscription`}>
                <Stack>
                    <Text>Are you sure you want to remove this Guard {selectedRemoveGuardRecordSubscription?.guardRecordSubscriptionType} Subscription?</Text>
                    <Group align='center' justify='space-between'>
                        <Button w='45%' color='pq-gray' onClick={guardRecordSubscriptionRemoveModalHandlers.close}>Cancel</Button>
                        <Button w='45%' color='pq-red' onClick={onRemoveGuardRecordSubscription}>Remove Subscription</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal onClose={notificationMethodRemoveModalHandlers.close} opened={notificationMethodRemoveModalOpened} title={`Remove ${selectedRemoveNotificationMethod?.notificationMethodType} Notification Method`}>
                <Stack>
                    <Text>Are you sure you want to remove this {selectedRemoveNotificationMethod?.notificationMethodType} Notification Method?</Text>
                    <Group align='center' justify='space-between'>
                        <Button w='45%' color='pq-gray' onClick={notificationMethodRemoveModalHandlers.close}>Cancel</Button>
                        <Button w='45%' fz={10} color='pq-red' onClick={onRemoveNotificationMethod}>Remove Notification Method</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal opened={resendNotificationMethodVerificationModalOpened} onClose={resendNotificationMethodVerificationModalHandlers.close} title="Resend Verification">
                <Stack>
                    <Text>Are you sure you want to resend the verification</Text>
                    <Group align='center' justify='space-between'>
                        <Button w='45%' color='pq-gray' onClick={resendNotificationMethodVerificationModalHandlers.close}>Cancel</Button>
                        <Button w='45%' fz={10} color='pq-red' onClick={() => {onResendVerification(selectedNotificationMethod?.notificationMethodId ?? 0); resendNotificationMethodVerificationModalHandlers.close();}}>Resend Verification</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}

export default Dashboard;